/**
 * GENERATED_ASSET — free-standing weathered lantern brace for the Hush Nook threshold.
 *
 * Ported from the batch001 kit's `existing.weathered_brace` binding (`static-kit/source/existingBindings.ts`), which
 * composed the game's own `drawTimberBoards` + `drawRopeSpan` into a short vertical plate, a horizontal arm, a
 * diagonal brace and two lashings, and mounted it on the arch's stone face. Here it stands on the ground instead:
 * the post runs to the frame bottom, the knee brace joins post and arm, rope lashings bind both joints, and an iron
 * J-hook hangs under the arm's end. Same timber palette mapping as the kit (primary=woodBase, shade=woodShadow,
 * stone=salt, iron=outline). Drawn directly in template-frame coordinates; the hook seat is the `hook` point a
 * hanging child (the three-window lantern's `hang`) coincides with. Decorative only: no collision, no platform.
 */
import type { ResolvedPalette } from '../../../../../shared/assets/hushStatic';
import type { TownPropPalette } from '../../../../../shared/scene/townPropTemplates';
import { drawRopeSpan, drawTimberBoards } from '../propDrawingPrimitives';

export interface Box { readonly x: number; readonly y: number; readonly w: number; readonly h: number }
export interface BraceRecipe {
  readonly id: string;
  readonly frame: { readonly w: number; readonly h: number };
  readonly post: Box;
  readonly arm: Box;
  /** Knee brace centerline from the post to the arm underside, board breadth `width`. */
  readonly knee: { readonly from: { x: number; y: number }; readonly to: { x: number; y: number }; readonly width: number };
  /** Iron J-hook: strap around the arm at `x`, stem down to `stemBottom`, bowl seat at `seat`, tip at `tip`. */
  readonly hook: {
    readonly x: number; readonly stemBottom: number; readonly seat: { x: number; y: number };
    readonly tip: { x: number; y: number }; readonly stroke: number;
  };
  readonly rope: number;
}

/** Frame 110 x 170: post at the left, arm reaching right, hook seat under the arm end. */
export const HUSH_LANTERN_BRACE: BraceRecipe = {
  id: 'hush.brace.weathered_ground.v1',
  frame: { w: 110, h: 170 },
  post: { x: 14, y: 9, w: 11, h: 161 },
  arm: { x: 11, y: 14, w: 96, h: 9 },
  knee: { from: { x: 19.5, y: 80 }, to: { x: 72, y: 21 }, width: 7 },
  hook: { x: 99, stemBottom: 36, seat: { x: 102.5, y: 43 }, tip: { x: 107, y: 36.5 }, stroke: 2.4 },
  rope: 1.7,
};

const num = (hex: string): number => Number.parseInt(hex.slice(1), 16);
const f = (v: number): string => String(Math.round(v * 1000) / 1000);

/** The kit's timber palette: the brace boards read as wood, the rope fibre as salt. */
export function braceTimberPalette(roles: ResolvedPalette): TownPropPalette {
  return {
    primary: num(roles.woodBase), shade: num(roles.woodShadow), roof: num(roles.clothBlue), roofEdge: num(roles.clothBlueLight),
    wood: num(roles.woodBase), woodEdge: num(roles.woodLight), stone: num(roles.salt), iron: num(roles.outline),
    glass: num(roles.warmCore), glassCore: num(roles.paperLight),
  };
}

export interface BracePlacement {
  readonly body: string;
  /** Frame-local hook seat (where a hung child's hangPoint must land). */
  readonly hook: { readonly x: number; readonly y: number };
  /** Painted foot contact: post bottom-center, on the frame floor. */
  readonly foot: { readonly x: number; readonly y: number };
  /** Conservative painted bounds (shapes + half strokes). */
  readonly paintBounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}

export function buildLanternBrace(r: BraceRecipe, roles: ResolvedPalette): BracePlacement {
  const tp = braceTimberPalette(roles);
  const { post, arm, knee, hook } = r;
  const outline = roles.outline;
  const edge = (b: Box): string => `<rect x="${f(b.x - 0.6)}" y="${f(b.y - 0.6)}" width="${f(b.w + 1.2)}" height="${f(b.h + 0.6 + (b.y + b.h >= r.frame.h ? 0 : 0.6))}" fill="${outline}" opacity="0.55"/>`;
  const dx = knee.to.x - knee.from.x, dy = knee.to.y - knee.from.y;
  const len = Math.hypot(dx, dy), deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const kneeBoard = { x: 0, y: -knee.width / 2, width: len, height: knee.width };
  const kneeG = `<g data-part="knee-brace" transform="translate(${f(knee.from.x)} ${f(knee.from.y)}) rotate(${f(deg)})">`
    + `<rect x="-0.6" y="${f(-knee.width / 2 - 0.6)}" width="${f(len + 1.2)}" height="${f(knee.width + 1.2)}" fill="${outline}" opacity="0.55"/>`
    + drawTimberBoards(kneeBoard, tp, { axis: 'horizontal', boards: 1 }) + '</g>';
  const postBody = `<g data-part="brace-post">${edge(post)}`
    + drawTimberBoards({ x: post.x, y: post.y, width: post.w, height: post.h }, tp, { axis: 'vertical', boards: 1 })
    // Damp, darker foot where the post meets the ground.
    + `<rect x="${f(post.x)}" y="${f(post.y + post.h - 14)}" width="${f(post.w)}" height="14" fill="${roles.woodShadow}" opacity="0.45"/>`
    + `<path d="M${f(post.x + 2)} ${f(post.y + 40)}l2 9M${f(post.x + 7)} ${f(post.y + 96)}l-1 11" fill="none" stroke="${roles.salt}" stroke-width="0.8" stroke-linecap="round" opacity="0.55"/>`
    + '</g>';
  const armBody = `<g data-part="brace-arm">${edge(arm)}`
    + drawTimberBoards({ x: arm.x, y: arm.y, width: arm.w, height: arm.h }, tp, { axis: 'horizontal', boards: 1 })
    + `<path d="M${f(arm.x + 30)} ${f(arm.y + 2.5)}h9M${f(arm.x + 62)} ${f(arm.y + 6.5)}h7" fill="none" stroke="${roles.salt}" stroke-width="0.8" stroke-linecap="round" opacity="0.5"/>`
    + '</g>';
  const lash = (a: { x: number; y: number }, b: { x: number; y: number }) => drawRopeSpan(a, b, tp, { sag: 1, thickness: r.rope });
  const cx = post.x + post.w / 2;
  const lashings = '<g data-part="brace-lashings">'
    + lash({ x: post.x - 2, y: arm.y + 4 }, { x: post.x + post.w + 2, y: arm.y + 6 })
    + lash({ x: post.x - 2, y: arm.y + 8 }, { x: post.x + post.w + 2, y: arm.y + 10 })
    + lash({ x: post.x - 1.5, y: knee.from.y - 4 }, { x: post.x + post.w + 1.5, y: knee.from.y - 1 })
    + lash({ x: knee.to.x - 6, y: arm.y - 1 }, { x: knee.to.x + 3, y: arm.y + arm.h + 1 })
    + '</g>';
  const h = hook;
  const hookD = `M${f(h.x)} ${f(arm.y + arm.h)}V${f(h.stemBottom)}Q${f(h.x)} ${f(h.seat.y)} ${f(h.seat.x)} ${f(h.seat.y)}`
    + `Q${f(h.tip.x)} ${f(h.seat.y)} ${f(h.tip.x)} ${f(h.tip.y)}`;
  const hookBody = '<g data-part="brace-hook">'
    + `<rect x="${f(h.x - 2.2)}" y="${f(arm.y - 1)}" width="4.4" height="${f(arm.h + 2)}" rx="0.8" fill="${outline}"/>`
    + `<path d="${hookD}" fill="none" stroke="${outline}" stroke-width="${f(h.stroke)}" stroke-linecap="round" stroke-linejoin="round"/>`
    + `<path d="M${f(h.x - 0.5)} ${f(arm.y + arm.h + 2)}V${f(h.stemBottom)}" fill="none" stroke="${roles.stoneLight}" stroke-width="0.7" stroke-linecap="round" opacity="0.6"/>`
    + '</g>';
  const shadow = `<ellipse data-part="brace-shadow" cx="${f(cx)}" cy="${f(r.frame.h - 1.5)}" rx="13" ry="1.5" fill="${outline}" opacity="0.18"/>`;
  // Paint order: shadow, knee (ends tuck under post and arm), post, arm, lashings, hook — as the kit drew it.
  const body = `<g data-part="hush-static-lantern-brace" data-recipe="${r.id}" data-integration="GENERATED_ASSET">`
    + shadow + kneeG + postBody + armBody + lashings + hookBody + '</g>';
  const xs = [post.x - 2 - r.rope / 2, cx - 13, h.tip.x + h.stroke / 2, arm.x + arm.w + 0.6];
  const ys = [post.y - 0.6, arm.y - 1 - r.rope, r.frame.h];
  return {
    body,
    hook: { x: h.seat.x, y: h.seat.y },
    foot: { x: cx, y: post.y + post.h },
    paintBounds: {
      x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys),
    },
  };
}
